/**
 * Wire contract between the monitor API and the dashboard.
 *
 * Imported by BOTH the server (src/) and the client (web/src/), so a field
 * renamed on one side fails to compile on the other. That is the whole point:
 * the previous vanilla-JS dashboard could drift from the API silently and only
 * fail at runtime, in the browser, during an incident.
 *
 * Dates are ISO-8601 strings here, not `Date`. The server's internal types use
 * `Date`; JSON.stringify turns them into strings on the way out, and modelling
 * that honestly is what stops `new Date(x)` bugs on the client.
 */

export type HealthStatus = 'ok' | 'degraded' | 'down';
export type Severity = 'P1' | 'P2' | 'P3' | 'P4';

/** One dependency inside a /health/ready payload. */
export interface DependencyCheck {
  status: HealthStatus | string;
  latency_ms?: number;
  error?: string;
  [key: string]: unknown;
}

/** The health-contract body a probed service returns. */
export interface ReadinessPayload {
  status?: string;
  service?: string;
  version?: string;
  uptime_s?: number;
  checks?: Record<string, DependencyCheck>;
  [key: string]: unknown;
}

export interface WireCheck {
  checkId: number;
  key: string;
  type: string;
  target: string | null;
  status: HealthStatus;
  since: string;
  lastLatencyMs: number | null;
  lastError: string | null;
  lastPayload: ReadinessPayload | null;
  severity: Severity;
}

export interface WireIncidentRef {
  id: number;
  severity: Severity;
  status: HealthStatus;
  openedAt: string;
  acked: boolean;
  isRollupChild: boolean;
}

export interface WireService {
  id: number;
  key: string;
  name: string;
  group: string;
  env: string;
  tier: number;
  host: string | null;
  deps: string[];
  runbook: string | null;
  maxSeverity: Severity | null;
  enabled: boolean;
  status: HealthStatus;
  checks: WireCheck[];
  uptime24h: number | null;
  openIncidentIds: number[];
  incidents: WireIncidentRef[];
  worstSeverity: Severity | null;
  inMaintenance: boolean;
}

export interface WireIncident {
  id: number;
  serviceId: number;
  checkId: number | null;
  dedupeKey: string;
  severity: Severity;
  status: HealthStatus;
  summary: string;
  openedAt: string;
  ackedBy: string | null;
  ackedAt: string | null;
  resolvedAt: string | null;
  rootIncidentId: number | null;
  lastNotifiedAt: string | null;
  notifyCount: number;
}

export interface WireAgent {
  hostKey: string;
  ts: string;
  ageS: number;
  processes: Array<{ name: string; status: string; restarts: number; memoryMb?: number }>;
  containers: Array<{ name: string; state: string; health?: string }>;
  vitals: Record<string, number>;
}

export interface WireHistoryPoint {
  ts: string;
  status: HealthStatus;
  latencyMs: number | null;
}

// --- Response envelopes ----------------------------------------------------

export interface StatusResponse {
  generatedAt: string;
  services: WireService[];
  openIncidents: WireIncident[];
  agents: WireAgent[];
}

export interface IncidentsResponse {
  incidents: WireIncident[];
}

export interface IncidentDetailResponse {
  incident: WireIncident;
  events: Array<{ ts: string; kind: string; detail: Record<string, unknown> }>;
}

/** Keyed by checkId. */
export interface HistoryResponse {
  history: Record<number, WireHistoryPoint[]>;
}

export interface CheckHistoryResponse {
  history: WireHistoryPoint[];
}

// --- Bloomberg terminal usage (/api/v1/bbg-usage) ---------------------------
// Proxied unchanged from the gateway's GET /usage/history, so these mirror the
// gateway's snake_case body rather than this API's camelCase.

export type BbgUsageMetric = 'new_securities' | 'requests' | 'hits';

export type BbgUsageCounts = Record<BbgUsageMetric, number>;

export interface BbgUsageCaller {
  caller: string;
  new_securities: number;
  requests: number;
  hits?: number;
}

/** One calendar day. Metrics are null before history started (unknown, not zero). */
export interface BbgUsageDay {
  date: string;
  new_securities: number | null;
  requests: number | null;
  hits: number | null;
  callers: BbgUsageCaller[];
}

export interface BbgTerminalUsage {
  today: { date: string } & BbgUsageCounts;
  baseline_avg: BbgUsageCounts;
  baseline_days_counted: number;
  /** today / baseline_avg; null when the baseline is 0. */
  ratio: Record<BbgUsageMetric, number | null>;
  /** Metrics at or over their surge threshold today. */
  surging: BbgUsageMetric[];
  /** Oldest first. */
  days: BbgUsageDay[];
}

export type BbgBlockKind = 'bloomberg_limit' | 'bloomberg_unhealthy_limit' | 'gateway_daily_cap';

export interface BbgBlockEvent {
  ts: string;
  worker_id: string;
  kind: BbgBlockKind | (string & {});
  detail: string;
  today: BbgUsageCounts;
  baseline_avg: BbgUsageCounts;
  baseline_days_counted: number;
}

export interface BbgUsageResponse {
  enabled: boolean;
  /** First day history exists; null if none. */
  since: string | null;
  baseline_days: number;
  /** Surge threshold per metric = max(surge_floors[m], surge_multiplier × baseline_avg[m]). */
  surge_multiplier: number;
  surge_floors: BbgUsageCounts;
  /** Keyed by worker id (terminal-1, terminal-2, …). */
  terminals: Record<string, BbgTerminalUsage>;
  /** Blocks, newest first. */
  events: BbgBlockEvent[];
}

/** Server-sent event names pushed on /api/v1/stream. */
export type StreamEvent = 'check' | 'incident';

export interface CheckChangedEvent {
  serviceKey: string;
  checkKey: string;
  status: HealthStatus;
}

// --- Shared display helpers ------------------------------------------------

/** Worst status wins when several checks roll up into one service tile. */
export function worstStatus(statuses: HealthStatus[]): HealthStatus {
  if (statuses.includes('down')) return 'down';
  if (statuses.includes('degraded')) return 'degraded';
  return 'ok';
}

/** Sort weight — unhealthy first, so nothing red hides below the fold. */
export const STATUS_RANK: Record<HealthStatus, number> = { down: 0, degraded: 1, ok: 2 };
